import { useState, useEffect, useMemo } from 'react';
import { Card, Button, message } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, OrderedListOutlined, EditOutlined, CheckOutlined } from '@ant-design/icons';
import { S3Image } from '@/components/S3Image';
import { GroupedResponses } from '@/features/laudo/types/checklist-response.types';

interface PhotoItem {
  id: string;
  questionId: string;
  questionTitle: string;
  photoIndex: number;
  fileUrl: string;
  fileHash: string;
  caption?: string;
}

interface PhotoOrderManagerProps {
  groupedResponses: GroupedResponses[];
  currentOrder?: string[];
  onOrderChange: (order: string[]) => void;
}

export function PhotoOrderManager({
  groupedResponses,
  currentOrder,
  onOrderChange,
}: PhotoOrderManagerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localOrder, setLocalOrder] = useState<string[]>([]);

  const allPhotos = useMemo(() => {
    const photos: PhotoItem[] = [];

    groupedResponses.forEach((group) => {
      group.sections.forEach((section) => {
        section.responses.forEach((response) => {
          if (response.question.answerType === 'Photo') {
            const answer = response.answer as any;
            const responsePhotos = answer?.photos || [];

            responsePhotos.forEach((photo: any, photoIdx: number) => {
              const photoId = `${response.question.id}-${photoIdx}`;
              photos.push({
                id: photoId,
                questionId: response.question.id,
                questionTitle: response.question.title,
                photoIndex: photoIdx,
                fileUrl: photo.fileUrl,
                fileHash: photo.fileHash,
                caption: photo.caption,
              });
            });
          }
        });
      });
    });

    return photos;
  }, [groupedResponses]);

  const orderedPhotos = useMemo(() => {
    if (!currentOrder || currentOrder.length === 0) {
      return allPhotos;
    }

    const photosById = new Map(allPhotos.map(p => [p.id, p]));
    const ordered: PhotoItem[] = [];

    currentOrder.forEach(id => {
      const photo = photosById.get(id);
      if (photo) {
        ordered.push(photo);
        photosById.delete(id);
      }
    });

    // Adiciona fotos novas que não estavam na ordem salva
    photosById.forEach(photo => {
      ordered.push(photo);
    });

    return ordered;
  }, [allPhotos, currentOrder]);

  // Inicializa ordem local quando começa a editar
  useEffect(() => {
    if (isEditing) {
      setLocalOrder(orderedPhotos.map(p => p.id));
    }
  }, [isEditing]);

  const handleMoveUp = (photoId: string) => {
    setLocalOrder(prev => {
      const idx = prev.indexOf(photoId);
      if (idx <= 0) return prev;

      const newOrder = [...prev];
      [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
      return newOrder;
    });
  };

  const handleMoveDown = (photoId: string) => {
    setLocalOrder(prev => {
      const idx = prev.indexOf(photoId);
      if (idx < 0 || idx >= prev.length - 1) return prev;

      const newOrder = [...prev];
      [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
      return newOrder;
    });
  };

  const handleSave = () => {
    onOrderChange(localOrder);
    setIsEditing(false);
    message.success('Ordem das fotos salva!');
  };

  const handleCancel = () => {
    setLocalOrder(orderedPhotos.map(p => p.id));
    setIsEditing(false);
  };

  // Fotos na ordem de edição
  const displayPhotos = useMemo(() => {
    if (!isEditing) {
      return orderedPhotos;
    }

    const photosById = new Map(allPhotos.map(p => [p.id, p]));
    return localOrder.map(id => photosById.get(id)).filter(Boolean) as PhotoItem[];
  }, [isEditing, orderedPhotos, localOrder, allPhotos]);

  if (allPhotos.length === 0) {
    return null;
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            <OrderedListOutlined style={{ marginRight: 8 }} />
            Ordem das Fotos no PDF ({allPhotos.length})
          </span>
          {!isEditing ? (
            <Button
              size="small"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setIsEditing(true)}
            >
              Editar Ordem
            </Button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="small" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button size="small" type="primary" icon={<CheckOutlined />} onClick={handleSave}>
                Salvar
              </Button>
            </div>
          )}
        </div>
      }
      style={{ marginTop: 24 }}
      bodyStyle={{ padding: isEditing ? 16 : 12 }}
    >
      {!isEditing ? (
        <div style={{ fontSize: 12, color: '#666' }}>
          As fotos aparecerão no PDF na ordem mostrada. Clique em "Editar Ordem" para reorganizar.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {displayPhotos.map((photo, idx) => (
            <div
              key={photo.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 8,
                backgroundColor: '#fafafa',
                borderRadius: 8,
                border: '1px solid #e8e8e8',
              }}
            >
              {/* Número da posição */}
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: '#1890ff',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </div>

              {/* Botões de reordenação */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={idx === 0}
                  onClick={() => handleMoveUp(photo.id)}
                  style={{ padding: '0 4px', height: 20, fontSize: 10 }}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={idx === displayPhotos.length - 1}
                  onClick={() => handleMoveDown(photo.id)}
                  style={{ padding: '0 4px', height: 20, fontSize: 10 }}
                />
              </div>

              {/* Thumbnail da foto */}
              <div style={{ flexShrink: 0 }}>
                <S3Image
                  src={photo.fileUrl}
                  documentId={photo.fileHash}
                  alt={photo.caption || photo.questionTitle}
                  width={50}
                  height={50}
                  preview={false}
                  style={{ borderRadius: 4, objectFit: 'cover' }}
                />
              </div>

              {/* Info da foto */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {photo.questionTitle}
                </div>
                {photo.caption && (
                  <div
                    style={{
                      fontSize: 11,
                      color: '#666',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {photo.caption}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
